import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useAxios } from '../context/AxiosContext';
import { SearchSelectItem } from '@td-client-frontend/common';
import {
  ActivitiesReponse,
  ActivityDetails,
  ActivityDetailType,
  ActivityType,
  ActivityTypesResponse,
  PartialActivityLogSearchQuery
} from '../@types/Audit';

export function useActivities(query: PartialActivityLogSearchQuery) {
  const { authAxios } = useAxios();
  const fetchActivities = useCallback(
    async (query: PartialActivityLogSearchQuery) => {
      const response = await authAxios.get<ActivitiesReponse>('/audit', {
        params: query
      });
      return response.data;
    },
    [authAxios]
  );
  return useQuery<ActivitiesReponse>(['activities', query], () => fetchActivities(query), {
    refetchOnWindowFocus: false
  });
}

export function useActivityById(activityId: string, type: ActivityDetailType) {
  const { authAxios } = useAxios();
  const pathTypes: Record<ActivityDetailType, string> = {
    group: 'audit',
    system: 'audit/system'
  };

  const getActivityById = useCallback(
    async (id: string) => {
      const response = await authAxios.get<ActivityDetails>(`/${pathTypes[type]}/${id}`);
      return response?.data;
    },
    [authAxios, pathTypes, type]
  );
  return useQuery<ActivityDetails>(['activity', activityId], () => getActivityById(activityId), {
    refetchOnWindowFocus: false,
    enabled: !!activityId,
    suspense: true
  });
}

export function useGetActivityTypes(selectedEmployerActivity: string, type: string) {
  const { authAxios } = useAxios();
  const fetchActivitiesTypes = useCallback(
    async ({ type }) => {
      const response = await authAxios.get<ActivityTypesResponse>(`/audit/audit-type/${type}`);
      return response.data;
    },
    [authAxios]
  );

  const { data: activityData } = useQuery<ActivityTypesResponse>(
    ['activity-type', selectedEmployerActivity, type],
    () => fetchActivitiesTypes({ type }),
    {
      refetchOnWindowFocus: false
    }
  );

  if (activityData?.tags && activityData?.types && selectedEmployerActivity) {
    const availableTags: string[] =
      activityData.types.find((type: ActivityType) => type.value === selectedEmployerActivity)?.tags || [];
    const tags = activityData.tags.reduce((acc: SearchSelectItem[], tag: SearchSelectItem): SearchSelectItem[] => {
      const filteredTag: SearchSelectItem = {
        ...tag,
        items: tag.items?.filter((tag: SearchSelectItem) => availableTags?.includes(tag.value))
      };
      if (!filteredTag.items || filteredTag.items.length) {
        acc.push(filteredTag);
      }
      return acc;
    }, []);
    return { tags, types: activityData.types };
  }
  return activityData;
}

export function useGetActivitySearch(field: string, searchValue: string, initialData: SearchSelectItem[] = []) {
  const { authAxios } = useAxios();

  const fetchActivitiesOptions = useCallback(
    async ({ field, searchValue }) => {
      const { data } = await authAxios.get<SearchSelectItem[]>(`/audit/${field}/unique`, {
        params: {
          searchValue
        }
      });
      return data;
    },
    [authAxios]
  );

  return useQuery<SearchSelectItem[]>(
    ['activity-search', field, searchValue],
    () => fetchActivitiesOptions({ field, searchValue }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: !!searchValue && searchValue.length >= 3,
      initialData
    }
  );
}
